import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Flame, Target, Sparkles, MapPin, ChevronRight } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api_getMyProfile, api_generateSession, api_listMySessions } from "@/lib/api";
import DeleteSessionButton from "@/components/DeleteSessionButton";

const TIME_CHOICES = [15, 30, 45, 60, 90, 120];
const LOCATIONS = [
  { id: "range", label: "Range" },
  { id: "short_game", label: "Short game" },
  { id: "putting", label: "Putting" },
  { id: "home", label: "Home" },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [time, setTime] = useState(30);
  const [locations, setLocations] = useState(["range"]);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, ss] = await Promise.all([api_getMyProfile(), api_listMySessions()]);
      setProfile(p);
      setSessions(ss);
      setTime(p.default_time_minutes || 30);
      const locs = Array.isArray(p.locations) ? p.locations : (p.location ? [p.location] : ["range"]);
      setLocations(locs.length ? locs : ["range"]);
    } catch (e) {
      toast.error("Could not load your profile.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleLocation = (id) => {
    setLocations((cur) => {
      if (cur.includes(id)) {
        const next = cur.filter((x) => x !== id);
        return next.length ? next : cur; // keep at least one
      }
      return [...cur, id];
    });
  };

  const startSession = async () => {
    setGenerating(true);
    try {
      const s = await api_generateSession({ time_minutes: time, locations });
      navigate(`/session/${s.id}`);
    } catch (e) {
      toast.error("Could not build a session. Try again.");
      setGenerating(false);
    }
  };

  const completed = sessions.filter((s) => s.status === "completed");

  if (!profile) {
    return (
      <AppShell>
        <div className="text-slate-500 text-center py-20">Loading your practice lab…</div>
      </AppShell>
    );
  }

  return (
    <AppShell profile={profile}>
      {/* Hero */}
      <section className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 bg-slate-900 text-white rounded-3xl p-8 relative overflow-hidden">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/60">Welcome back, {profile.name}</p>
          <h1 className="mt-3 text-3xl sm:text-4xl font-heading font-black tracking-tight leading-tight">
            Your practice identity:
          </h1>
          <p className="mt-3 text-2xl sm:text-3xl font-heading font-black text-white" data-testid="practice-identity">
            {profile.identity || "focused improver"}
          </p>
          <p className="mt-5 text-white/70 max-w-lg leading-relaxed">
            Build a session tailored to your time, location, and biggest leaks. Every rep moves the needle.
          </p>

          {/* Time picker */}
          <div className="mt-8 bg-white/5 border border-white/10 rounded-2xl p-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/60">How much time today?</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {TIME_CHOICES.map((t) => (
                <button
                  key={t}
                  data-testid={`dash-time-${t}`}
                  onClick={() => setTime(t)}
                  className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${time === t ? "bg-white text-[#0B2545]" : "bg-white/10 text-white/80 hover:bg-white/20"}`}
                >
                  {t} min
                </button>
              ))}
            </div>
            <p className="mt-5 text-[11px] font-bold uppercase tracking-[0.2em] text-white/60">Where? <span className="font-semibold normal-case tracking-normal text-white/40">(tap to pick one or more)</span></p>
            <div className="mt-3 flex flex-wrap gap-2">
              {LOCATIONS.map((l) => {
                const active = locations.includes(l.id);
                return (
                  <button
                    key={l.id}
                    data-testid={`dash-loc-${l.id}`}
                    onClick={() => toggleLocation(l.id)}
                    className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${active ? "bg-white text-slate-900" : "bg-white/10 text-white/80 hover:bg-white/20"}`}
                  >
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {l.label}</span>
                  </button>
                );
              })}
            </div>
            <button
              onClick={startSession}
              data-testid="dash-start-session"
              disabled={generating}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-white text-[#0B2545] font-bold text-base hover:bg-slate-100 active:scale-[0.98] transition-all disabled:opacity-60"
            >
              {generating ? "Building your session…" : "Build today's session"} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#0B2545] text-white flex items-center justify-center">
                <Flame className="w-5 h-5" />
              </div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Current streak</p>
            </div>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="text-5xl font-heading font-black" data-testid="streak-count">{profile.streak_count || 0}</span>
              <span className="text-slate-500 font-semibold">days</span>
            </div>
            <p className="mt-2 text-sm text-slate-600 leading-snug">
              {profile.streak_count > 0 ? "Show up tomorrow to keep it alive." : "Complete your first session today to start a streak."}
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center">
                <Target className="w-5 h-5" />
              </div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Sessions complete</p>
            </div>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="text-5xl font-heading font-black" data-testid="total-sessions">{completed.length}</span>
              <span className="text-slate-500 font-semibold">total</span>
            </div>
            <p className="mt-2 text-sm text-slate-600 leading-snug">Every rep compounds. Stay consistent.</p>
          </div>
        </div>
      </section>

      {/* Recent sessions */}
      <section>
        <div className="flex items-end justify-between mb-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Recent</p>
            <h3 className="text-2xl font-heading font-black">Past sessions</h3>
          </div>
          <button onClick={() => navigate("/history")} data-testid="dash-view-all" className="text-sm font-bold text-slate-700 hover:text-[#0B2545] inline-flex items-center gap-1">
            View all <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        {sessions.length === 0 ? (
          <div className="bg-slate-50 border border-dashed border-slate-300 rounded-2xl p-10 text-center">
            <Sparkles className="w-6 h-6 text-[#0B2545] mx-auto" />
            <p className="mt-3 font-heading font-bold text-lg">No sessions yet</p>
            <p className="text-sm text-slate-600 mt-1">Build your first session above to get started.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {sessions.slice(0, 4).map((s) => {
              const go = () => navigate(s.status === "completed" ? `/reflection/${s.id}` : `/session/${s.id}`);
              return (
                <div
                  key={s.id}
                  role="button"
                  tabIndex={0}
                  onClick={go}
                  onKeyDown={(e) => { if (e.key === "Enter") go(); }}
                  data-testid={`session-card-${s.id}`}
                  className="relative text-left bg-white border border-slate-200 rounded-2xl p-5 hover:border-slate-300 hover:-translate-y-0.5 transition-all cursor-pointer"
                >
                  <div className="absolute top-3 right-3">
                    <DeleteSessionButton sessionId={s.id} onDeleted={load} size="sm" />
                  </div>
                  <div className="flex items-center justify-between pr-8">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                      {new Date(s.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </p>
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-md ${s.status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {s.status}
                    </span>
                  </div>
                  <p className="mt-2 font-heading font-bold text-lg leading-tight">{s.title}</p>
                  <p className="mt-1 text-sm text-slate-600 line-clamp-2">{s.summary}</p>
                  <div className="mt-3 flex gap-2 text-xs font-bold text-slate-600">
                    <span className="bg-slate-100 px-2 py-1 rounded-md">{s.drills.length} drills</span>
                    <span className="bg-slate-100 px-2 py-1 rounded-md">{s.time_minutes}m</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Back to landing page */}
      <div className="mt-12 flex justify-center">
        <button
          onClick={() => navigate("/")}
          data-testid="dash-back-home"
          className="btn-ghost text-sm py-3 px-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </button>
      </div>
    </AppShell>
  );
}
